import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CircularProgress from "@mui/material/CircularProgress";
import { createUser, resendVerification } from "../api/apiClient";
import { useTenant } from "../contexts/TenantContext";
import AuthLayout, { AuthAlert } from "./auth/AuthLayout";
import AuthField from "./auth/AuthField";
import { passwordMeetsRules, passwordRules } from "./auth/passwordRules";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_USERNAME = 3;
// Placeholders until the real Terms of Use and Privacy Policy pages are supplied.
const TERMS_URL = "#";
const PRIVACY_URL = "#";

function formatApiError(err) {
  const data = err?.response?.data;
  const fieldErrors = data?.errors
    ? Object.entries(data.errors)
        .map(([field, msg]) => `${field}: ${msg}`)
        .join(", ")
    : null;
  return fieldErrors || data?.error || err?.message || "Failed to create user";
}

function Steps({ step }) {
  return (
    <div className="auth-steps" aria-label={`Step ${step} of 2`}>
      <span className={step === 1 ? "on" : "dn"}>
        <i>{step > 1 ? <CheckIcon sx={{ fontSize: 11 }} /> : "1"}</i>Account
      </span>
      <span className="ln" />
      <span className={step === 2 ? "on" : ""}>
        <i>2</i>Details
      </span>
    </div>
  );
}

function ConfirmEmail({ email, onChange, onSignIn }) {
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    setResent(true);
    try {
      await resendVerification(email);
    } catch {
      // Never reveals whether the address exists; the throttle may also skip the send.
    }
  };

  return (
    <>
      <div className="auth-ico">
        <MailOutlineIcon />
      </div>
      <h1>Confirm your email</h1>
      <p className="auth-sub">
        We sent a confirmation link to <span className="auth-mail">{email}</span>. Open it to activate your
        account. The link expires in 24 hours.
      </p>
      <div className="auth-acts">
        <button type="button" className="auth-btn auth-btn--o" onClick={handleResend} disabled={resent}>
          {resent ? "Link sent" : "Resend link"}
        </button>
        <button type="button" className="auth-btn auth-btn--p" onClick={onSignIn}>
          Back to sign in
        </button>
      </div>
      <div className="auth-alt">
        Wrong email?{" "}
        <button type="button" className="auth-link" onClick={onChange}>
          Change it
        </button>
      </div>
    </>
  );
}

export default function UserCreation() {
  const navigate = useNavigate();
  const { domainTenant } = useTenant();
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [memberId, setMemberId] = useState("");
  const [terms, setTerms] = useState(false);
  const [tried, setTried] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [sent, setSent] = useState(false);

  const errors = {
    firstName: firstName.trim() ? "" : "Required.",
    lastName: lastName.trim() ? "" : "Required.",
    username: username.trim().length < MIN_USERNAME ? `At least ${MIN_USERNAME} characters.` : "",
    email: EMAIL_RE.test(email.trim()) ? "" : "Enter a valid email address.",
    password: passwordMeetsRules(password) ? "" : "Password doesn't meet the requirements below.",
  };
  const step1Invalid = Object.values(errors).some(Boolean);

  const handleContinue = (e) => {
    e.preventDefault();
    setTried(true);
    if (step1Invalid) return;
    setTried(false);
    setMessage(null);
    setStep(2);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTried(true);
    setMessage(null);
    if (!terms) return;

    setLoading(true);
    createUser({
      username: username.trim(),
      email: email.trim(),
      password,
      roles: ["STUDENT"],
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      memberId: memberId.trim() || undefined,
      termsAccepted: true,
    })
      .then((created) => {
        // The server decides: with forced verification off there is nothing to confirm.
        if (created?.emailVerificationRequired) {
          setSent(true);
        } else {
          navigate("/login");
        }
      })
      .catch((err) => setMessage({ severity: "error", text: formatApiError(err) }))
      .finally(() => setLoading(false));
  };

  const backToStep1 = () => {
    setSent(false);
    setTried(false);
    setMessage(null);
    setStep(1);
  };

  if (sent) {
    return (
      <AuthLayout domainTenant={domainTenant}>
        <ConfirmEmail email={email.trim()} onChange={backToStep1} onSignIn={() => navigate("/login")} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout domainTenant={domainTenant}>
      <Steps step={step} />
      <h1>{step === 1 ? "Create an account" : "A few more details"}</h1>
      <p className="auth-sub">
        {step === 1 ? (
          <>
            Register as a student
            {domainTenant?.type === "tenant" ? ` at ${domainTenant.name}` : ""}. You&apos;ll use this to
            enroll in courses and track your certifications.
          </>
        ) : (
          "Almost done. Review the terms and finish creating your account."
        )}
      </p>

      {message && <AuthAlert>{message.text}</AuthAlert>}

      {step === 1 ? (
        <form className="auth-form" onSubmit={handleContinue} noValidate>
          <div className="auth-row2">
            <AuthField
              id="firstName"
              label="First name"
              value={firstName}
              onChange={setFirstName}
              autoComplete="given-name"
              error={tried ? errors.firstName : ""}
            />
            <AuthField
              id="lastName"
              label="Last name"
              value={lastName}
              onChange={setLastName}
              autoComplete="family-name"
              error={tried ? errors.lastName : ""}
            />
          </div>
          <AuthField
            id="username"
            label="Username"
            value={username}
            onChange={setUsername}
            autoComplete="username"
            error={tried ? errors.username : ""}
          />
          <AuthField
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            hint="We'll send a confirmation link here."
            error={tried ? errors.email : ""}
          />
          <div className="auth-fl">
            <AuthField
              id="password"
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              error={tried ? errors.password : ""}
            />
            <ul className="auth-req" aria-label="Password requirements">
              {passwordRules(password).map(([ok, text]) => (
                <li key={text} className={ok ? "ok" : ""}>
                  <i>{ok && <CheckIcon sx={{ fontSize: 10 }} />}</i>
                  {text}
                </li>
              ))}
            </ul>
          </div>
          <button className="auth-btn auth-btn--p" type="submit">
            Continue
            <ArrowForwardIcon sx={{ fontSize: 15 }} />
          </button>
        </form>
      ) : (
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <AuthField
            id="memberId"
            label="Member or employee ID"
            optional
            value={memberId}
            onChange={setMemberId}
            hint="Helps your coordinator match your existing records."
          />
          <label className="auth-ck">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
            <span>
              I agree to the <a href={TERMS_URL}>Terms of Use</a> and <a href={PRIVACY_URL}>Privacy Policy</a>.
            </span>
          </label>
          {tried && !terms && (
            <small className="auth-err-line" role="alert">
              You need to accept the terms to continue.
            </small>
          )}
          <div className="auth-acts">
            <button type="button" className="auth-btn auth-btn--o" onClick={backToStep1}>
              <ArrowBackIcon sx={{ fontSize: 15 }} />
              Back
            </button>
            <button className="auth-btn auth-btn--p" type="submit" disabled={loading}>
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} aria-label="Creating account" />
              ) : (
                "Create account"
              )}
            </button>
          </div>
        </form>
      )}

      <div className="auth-alt">
        Already have an account?{" "}
        <button type="button" className="auth-link" onClick={() => navigate("/login")}>
          Sign in
        </button>
      </div>
    </AuthLayout>
  );
}
